import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PALETTES } from "@/lib/chartTheme";
import {
  AlignStartVertical, AlignCenterVertical, AlignEndVertical,
  AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal,
  Columns3, Rows3, Group, Ungroup, CopyPlus, EyeOff, Trash2, Paintbrush, X, Boxes, FolderInput,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ALIGN_BTNS = [
  { mode: "left", icon: AlignStartVertical, label: "Align left" },
  { mode: "hcenter", icon: AlignCenterVertical, label: "Align center" },
  { mode: "right", icon: AlignEndVertical, label: "Align right" },
  { mode: "top", icon: AlignStartHorizontal, label: "Align top" },
  { mode: "vmiddle", icon: AlignCenterHorizontal, label: "Align middle" },
  { mode: "bottom", icon: AlignEndHorizontal, label: "Align bottom" },
];

function TB({ onClick, title, testid, children, danger }) {
  return (
    <button data-testid={testid} title={title} onClick={onClick}
      className={cn("p-1.5 rounded-lg transition-colors", danger ? "text-[color:var(--danger)] hover:bg-[color:rgba(226,75,91,0.10)]" : "text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--brand-ocean)]")}>
      {children}
    </button>
  );
}

const Sep = () => <span className="w-px h-5 bg-[color:var(--glass-border)] mx-0.5" />;

export function MultiSelectToolbar({ count, allGrouped, onAlign, onGroup, onUngroup, onCombine, onMoveInto, containers, onDuplicate, onHide, onDelete, onBulkFormat, onClear }) {
  return (
    <div data-testid="multiselect-toolbar"
      className="sticky top-2 z-40 mx-auto w-fit flex items-center gap-0.5 rounded-full border border-[color:var(--glass-border)] bg-[color:var(--surface)] shadow-lg px-2 py-1">
      <span data-testid="multiselect-count" className="text-[11px] font-semibold text-[color:var(--brand-ocean)] px-1.5">{count} selected</span>
      <Sep />
      {ALIGN_BTNS.map((b) => (
        <TB key={b.mode} testid={`align-${b.mode}`} title={b.label} onClick={() => onAlign(b.mode)}><b.icon className="w-4 h-4" /></TB>
      ))}
      <Sep />
      <TB testid="distribute-h" title="Distribute horizontally (3+)" onClick={() => onAlign("dist-h")}><Columns3 className="w-4 h-4" /></TB>
      <TB testid="distribute-v" title="Distribute vertically (3+)" onClick={() => onAlign("dist-v")}><Rows3 className="w-4 h-4" /></TB>
      <Sep />
      {allGrouped
        ? <TB testid="ungroup-btn" title="Ungroup (Ctrl+Shift+G)" onClick={onUngroup}><Ungroup className="w-4 h-4" /></TB>
        : <TB testid="group-btn" title="Group (Ctrl+G)" onClick={onGroup}><Group className="w-4 h-4" /></TB>}
      {onCombine && <TB testid="combine-btn" title="Combine into a container widget" onClick={onCombine}><Boxes className="w-4 h-4" /></TB>}
      {containers && containers.length > 0 && onMoveInto && (
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="move-into-container-btn" title="Move selection into an existing container" className="p-1.5 rounded-lg text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--brand-ocean)]"><FolderInput className="w-4 h-4" /></button>
          </PopoverTrigger>
          <PopoverContent align="center" className="w-56 p-1.5 bg-[color:var(--surface)]">
            <div className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] px-2 py-1">Move {count} into…</div>
            {containers.map((c) => (
              <button key={c.id} data-testid={`move-into-${c.id}`} onClick={() => onMoveInto(c.id)}
                className="w-full text-left text-xs px-2 py-1.5 rounded-lg hover:bg-[color:var(--surface-2)] text-[color:var(--ink-2)] flex items-center gap-2">
                <Boxes className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />{c.title || "Container"}
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}
      <BulkFormatPopover onApply={onBulkFormat} />
      <Sep />
      <TB testid="multiselect-duplicate" title="Duplicate (Ctrl+D)" onClick={onDuplicate}><CopyPlus className="w-4 h-4" /></TB>
      <TB testid="multiselect-hide" title="Hide widgets" onClick={onHide}><EyeOff className="w-4 h-4" /></TB>
      <TB testid="multiselect-delete" title="Delete (Del)" onClick={onDelete} danger><Trash2 className="w-4 h-4" /></TB>
      <Sep />
      <TB testid="multiselect-clear" title="Clear selection (Esc)" onClick={onClear}><X className="w-4 h-4" /></TB>
    </div>
  );
}

function BulkFormatPopover({ onApply }) {
  const [palette, setPalette] = React.useState("");
  const [legend, setLegend] = React.useState(true);
  const [labels, setLabels] = React.useState(false);
  const [grid, setGrid] = React.useState(true);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="bulk-format-btn" title="Bulk formatting"
          className="p-1.5 rounded-lg text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)] hover:text-[color:var(--brand-ocean)]">
          <Paintbrush className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-3" align="center">
        <div className="text-xs font-semibold text-[color:var(--ink)]">Apply formatting to selection</div>
        <div>
          <Label className="text-[11px] text-[color:var(--ink-3)]">Color palette</Label>
          <Select value={palette} onValueChange={setPalette}>
            <SelectTrigger data-testid="bulk-format-palette" className="mt-1 h-8 bg-[color:var(--surface)]"><SelectValue placeholder="Keep current" /></SelectTrigger>
            <SelectContent>{Object.keys(PALETTES).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {[
          { label: "Show legend", v: legend, set: setLegend, id: "bulk-legend" },
          { label: "Show data labels", v: labels, set: setLabels, id: "bulk-labels" },
          { label: "Show grid lines", v: grid, set: setGrid, id: "bulk-grid" },
        ].map((r) => (
          <div key={r.id} className="flex items-center justify-between">
            <Label className="text-xs text-[color:var(--ink-2)]">{r.label}</Label>
            <Switch data-testid={r.id} checked={r.v} onCheckedChange={r.set} />
          </div>
        ))}
        <button data-testid="bulk-format-apply"
          onClick={() => onApply({ ...(palette ? { palette } : {}), showLegend: legend, showLabels: labels, showGrid: grid })}
          className="w-full h-8 rounded-lg bg-[color:var(--brand-ocean)] text-white text-xs font-medium hover:opacity-90">
          Apply to selected
        </button>
      </PopoverContent>
    </Popover>
  );
}
